/**
 * Copyright (C) 2026 Jugendstil, SLU - All Rights Reserved
 *
 * Unauthorized copying of this file, via any medium is strictly prohibited. Proprietary and confidential.
 */
import { dateFromValue, monthFromDate, valueFromDate } from '../internal/date.js';
import { firstWeekday, pageLocale } from '../internal/locale.js';
import { register } from '../registry.js';

/*
 * The month grid behind `.wp-calendar`.
 *
 * Drawn from the `<input type="date">` it wraps and written straight back to it, so the form posts
 * the same field it always did and a page with no script keeps the browser's own picker. The input
 * is the state; this holds none of its own beyond which month is on screen, which lives on the
 * element as `data-month` so that a repaint after a Turbo swap resumes where the reader was rather
 * than jumping back to the selected day's month.
 *
 * Every string comes from `Intl.DateTimeFormat` in the page's language. A calendar that shipped
 * its own month and weekday names would be twelve plus seven strings per language to translate and
 * to keep in step with the dates beside them.
 */

/** How the arrow keys move through the month. PageUp and PageDown move by whole months. */
const STEPS: Record<string, number> = {
    ArrowDown: 7,
    ArrowLeft: -1,
    ArrowRight: 1,
    ArrowUp: -7,
    PageDown: 1,
    PageUp: -1
};

function blankCell(): HTMLSpanElement {
    const cell = document.createElement('span');
    cell.className = 'wp-calendar-blank';
    cell.setAttribute('aria-hidden', 'true');

    return cell;
}

/**
 * How many months one press of a step button moves.
 *
 * A month unless the button says otherwise, which is every calendar but the birthdate one: its
 * dates are decades back, and a month at a time is four hundred presses to reach them.
 */
function stepOf(button: HTMLElement): number {
    return Number(button.dataset.wpCalendarMonths ?? '1') || 1;
}

/** The month a calendar is showing, falling back the way a freshly rendered one has to. */
function shownMonth(calendar: HTMLElement, selected: Date | null, min: Date | null): Date {
    return dateFromValue(`${calendar.dataset.month ?? ''}-01`) ?? selected ?? min ?? new Date();
}

export function paintCalendar(calendar: HTMLElement): void {
    const input = calendar.querySelector<HTMLInputElement>('[data-wp-calendar-input]');
    const grid = calendar.querySelector<HTMLElement>('[data-wp-calendar-grid]');
    const label = calendar.querySelector<HTMLElement>('[data-wp-calendar-month]');
    if (input === null || grid === null || label === null) {
        return;
    }

    const locale = pageLocale();
    const selected = dateFromValue(input.value);
    const min = dateFromValue(input.min);
    const max = dateFromValue(input.max);
    const shown = shownMonth(calendar, selected, min);
    const first = new Date(shown.getFullYear(), shown.getMonth(), 1, 12);
    const today = valueFromDate(new Date());

    calendar.dataset.month = monthFromDate(first);
    calendar.dataset.enhanced = 'true';
    label.textContent = new Intl.DateTimeFormat(locale, { month: 'long', year: 'numeric' }).format(first);

    const weekdayFormat = new Intl.DateTimeFormat(locale, { weekday: 'narrow' });
    const dayLabelFormat = new Intl.DateTimeFormat(locale, { dateStyle: 'full' });
    const start = firstWeekday(locale);
    const offset = (first.getDay() - start + 7) % 7;
    const length = new Date(first.getFullYear(), first.getMonth() + 1, 0).getDate();

    grid.replaceChildren();

    for (let index = 0; index < 7; index++) {
        const heading = document.createElement('span');
        heading.className = 'wp-calendar-weekday';

        /* 4 January 1970 was a Sunday, so this walks a week from whichever day the locale starts
           on without needing a day-name table of its own.
           The trailing full stop goes: several languages abbreviate a weekday with one ("dl.",
           "dt."), and in a column heading over a month the mark says nothing that the column does
           not. `veryShortWeekdaySymbols`, which is what the picker this is a port of draws, has no
           stops either. */
        heading.textContent = weekdayFormat
            .format(new Date(1970, 0, 4 + ((start + index) % 7), 12))
            .replace(/\.$/, '');
        heading.setAttribute('aria-hidden', 'true');
        grid.append(heading);
    }

    /* The month takes the rows it needs and no more, so the card is shorter for a month that
       starts on a Monday than for one that spills into a sixth week - which is what the picker
       this is a port of does. The cells before the first of the month are empty rather than the
       previous month's dates: a date that cannot be chosen sitting among dates that can is worse
       than a gap. */
    const cells: HTMLElement[] = [];
    for (let blank = 0; blank < offset; blank++) {
        cells.push(blankCell());
    }

    for (let day = 1; day <= length; day++) {
        const date = new Date(first.getFullYear(), first.getMonth(), day, 12);
        const value = valueFromDate(date);
        const button = document.createElement('button');

        button.className = 'wp-calendar-day';
        button.type = 'button';
        button.textContent = String(day);
        button.dataset.wpCalendarDay = value;
        button.setAttribute('aria-label', dayLabelFormat.format(date));
        button.setAttribute('aria-pressed', String(value === input.value));
        button.disabled = (min !== null && date < min) || (max !== null && date > max);

        if (value === today) {
            button.dataset.today = 'true';
        }

        /* One stop in the tab order for the whole month, as a grid of controls should be: the
           chosen day, or the first that can be chosen. The arrows move within it from there. */
        button.tabIndex = -1;

        cells.push(button);
    }

    grid.append(...cells);

    const days = Array.from(grid.querySelectorAll<HTMLButtonElement>('.wp-calendar-day'));
    const focusable =
        days.find((it) => it.getAttribute('aria-pressed') === 'true' && !it.disabled) ??
        days.find((it) => !it.disabled);
    if (focusable !== undefined) {
        focusable.tabIndex = 0;
    }

    /* Each step is judged by its own reach, not by the month next door. A year arrow asked whether
       the next month was still allowed would stay lit at the edge of the range and carry the
       reader into a grid where every day is disabled. */
    calendar.querySelectorAll<HTMLButtonElement>('[data-wp-calendar-previous]').forEach((button) => {
        const step = stepOf(button);

        button.disabled = min !== null && new Date(first.getFullYear(), first.getMonth() - step + 1, 0, 12) < min;
    });

    calendar.querySelectorAll<HTMLButtonElement>('[data-wp-calendar-next]').forEach((button) => {
        const step = stepOf(button);

        button.disabled = max !== null && new Date(first.getFullYear(), first.getMonth() + step, 1, 12) > max;
    });
}

export function shiftCalendarMonth(calendar: HTMLElement, months: number): void {
    const shown = dateFromValue(`${calendar.dataset.month ?? ''}-01`) ?? new Date();

    calendar.dataset.month = monthFromDate(new Date(shown.getFullYear(), shown.getMonth() + months, 1, 12));
    paintCalendar(calendar);
}

export function paint(root: ParentNode): void {
    root.querySelectorAll<HTMLElement>('.wp-calendar').forEach(paintCalendar);
}

export function listen(): void {
    document.addEventListener('click', (event) => {
        const target = event.target;
        if (!(target instanceof Element)) {
            return;
        }

        const step = target.closest<HTMLElement>('[data-wp-calendar-previous], [data-wp-calendar-next]');
        if (step !== null) {
            event.preventDefault();

            const calendar = step.closest<HTMLElement>('.wp-calendar');
            if (calendar !== null) {
                const months = stepOf(step);

                shiftCalendarMonth(calendar, step.matches('[data-wp-calendar-previous]') ? -months : months);
            }

            return;
        }

        const day = target.closest('[data-wp-calendar-day]');
        if (day === null) {
            return;
        }

        event.preventDefault();

        const calendar = day.closest<HTMLElement>('.wp-calendar');
        const input = calendar?.querySelector<HTMLInputElement>('[data-wp-calendar-input]');
        if (calendar === null || calendar === undefined || input === null || input === undefined) {
            return;
        }

        input.value = day.getAttribute('data-wp-calendar-day') ?? '';
        paintCalendar(calendar);

        /* The value was set in script, so nothing else would hear about it. Whatever listens to
           this field is listening for the date to change, and it just changed. */
        input.dispatchEvent(new Event('change', { bubbles: true }));
    });

    /* Arrows walk the month and roll into the next one, which is what makes 42 buttons one control
       rather than 42 tab stops. */
    document.addEventListener('keydown', (event) => {
        const target = event.target;
        if (!(target instanceof Element)) {
            return;
        }

        const day = target.closest('[data-wp-calendar-day]');
        if (day === null) {
            return;
        }

        const step = STEPS[event.key];
        if (step === undefined) {
            return;
        }

        event.preventDefault();

        const calendar = day.closest<HTMLElement>('.wp-calendar');
        const from = dateFromValue(day.getAttribute('data-wp-calendar-day'));
        if (calendar === null || from === null) {
            return;
        }

        const wanted =
            event.key === 'PageUp' || event.key === 'PageDown'
                ? new Date(from.getFullYear(), from.getMonth() + step, from.getDate(), 12)
                : new Date(from.getFullYear(), from.getMonth(), from.getDate() + step, 12);

        calendar.dataset.month = monthFromDate(wanted);
        paintCalendar(calendar);

        const landed = calendar.querySelector<HTMLButtonElement>(
            `[data-wp-calendar-day="${valueFromDate(wanted)}"]`
        );
        if (landed !== null) {
            landed.tabIndex = 0;
            landed.focus();
        }
    });

    register(paint);
}
